import { z } from "zod"
import { isAxiosError } from "axios"
import type Profile from "@/types/profile"
import {
  ACCOUNT_TOKEN_KEY,
  ACCOUNT_TOKEN_EXPIRES_KEY,
  PROFILE_KEY,
} from "@/lib/account-token"

export const GUEST_SESSION_KEY = "meucontroleai:guest-session-v1"
export const GUEST_SESSION_EVENT = "meucontroleai:guest-session-change"

const guestUserSchema = z.object({
  id: z.string().uuid().refine(value => value !== "00000000-0000-0000-0000-000000000000"),
  name: z.string().min(1).max(200),
  phone: z.string().regex(/^[0-9]{10,15}$/),
})
const credentialSchema = z.string().min(1).max(8192)
const responseSchema = z.object({
  token: credentialSchema,
  expires_in: z.number().int().positive().max(86400),
  recovery_token: credentialSchema,
  recovery_expires_in: z.number().int().positive().max(30 * 86400),
  session_kind: z.enum(["guest_v1", "customer_v1"]),
  phone_verified: z.literal(false),
  user: guestUserSchema,
})
const storedSchema = z.object({
  sessionKind: z.enum(["guest_v1", "customer_v1"]).default("guest_v1"),
  user: guestUserSchema,
  accessToken: credentialSchema,
  accessExpiresAt: z.number().finite().positive(),
  recoveryToken: credentialSchema,
  recoveryExpiresAt: z.number().finite().positive(),
})

type StoredGuest = z.infer<typeof storedSchema>
let recovery: { snapshot: string; promise: Promise<string | null> } | null = null
let epoch = 0
let unavailableSnapshot: string | null = null

if (typeof window !== "undefined") {
  window.addEventListener("storage", event => {
    if (event.key === GUEST_SESSION_KEY || event.key === ACCOUNT_TOKEN_KEY || event.key === null) epoch++
  })
}

function notify() {
  window.dispatchEvent(new Event(GUEST_SESSION_EVENT))
}

function readGuest(): StoredGuest | null {
  try {
    const raw = localStorage.getItem(GUEST_SESSION_KEY)
    if (!raw) return null
    const result = storedSchema.safeParse(JSON.parse(raw))
    return result.success ? result.data : null
  } catch {
    return null
  }
}

function tokenSubject(token: string): string | null {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    const encoded = parts[1].replace(/-/g, "+").replace(/_/g, "/")
    const claims: unknown = JSON.parse(atob(encoded))
    const parsed = z.object({
      sub: z.string().uuid(),
      session_kind: z.enum(["guest_v1", "customer_v1"]),
      iss: z.literal("customer-service-gateway"),
      aud: z.literal("customer-access-v1"),
    }).safeParse(claims)
    return parsed.success ? parsed.data.sub : null
  } catch {
    return null
  }
}

/** Returns the customer profile declared by the last validated server credential response. */
export function guestProfile(): Profile | null {
  const stored = readGuest()
  if (!stored) return null
  return { id: stored.user.id, name: stored.user.name, phone: { number: stored.user.phone }, addresses: [], sessionKind: stored.sessionKind, phoneVerified: false }
}

/** Reports whether this browser still holds an unexpired guest recovery credential. */
export function hasRecoverableGuestSession(): boolean {
  const stored = readGuest()
  return !!stored && stored.recoveryExpiresAt > Date.now()
}

/** Reports a temporary recovery failure for the unchanged local guest. */
export function guestRecoveryUnavailable(): boolean {
  return hasRecoverableGuestSession() && unavailableSnapshot !== null &&
    localStorage.getItem(GUEST_SESSION_KEY) === unavailableSnapshot
}

/** Identifies a retryable recovery failure without treating the guest as absent. */
export class GuestRecoveryUnavailableError extends Error {
  constructor() {
    super("Não foi possível recuperar sua sessão agora. Tente novamente.")
    this.name = "GuestRecoveryUnavailableError"
  }
}

/** Invalidates pending recovery and deletes this browser's guest credentials. */
export function clearGuestSession(): void {
  unavailableSnapshot = null
  epoch++
  localStorage.removeItem(GUEST_SESSION_KEY)
  localStorage.removeItem(ACCOUNT_TOKEN_KEY)
  localStorage.removeItem(ACCOUNT_TOKEN_EXPIRES_KEY)
  localStorage.removeItem(PROFILE_KEY)
  notify()
}

/** Captures the current local session revision for an asynchronous login. */
export function guestSessionEpoch(): number {
  return epoch
}

/** Stores a normal customer login/recovery response after validating its subject and contract. */
export function storeGuestSession(value: unknown, expectedEpoch = epoch, expectedSubject?: string): Profile {
  const body = responseSchema.parse(value)
  if (epoch !== expectedEpoch || (expectedSubject && body.user.id !== expectedSubject) || tokenSubject(body.token) !== body.user.id) {
    throw new Error("A sessão mudou. Tente novamente.")
  }
  unavailableSnapshot = null
  const now = Date.now()
  const stored: StoredGuest = {
    sessionKind: body.session_kind,
    user: body.user,
    accessToken: body.token,
    accessExpiresAt: now + body.expires_in * 1000,
    recoveryToken: body.recovery_token,
    recoveryExpiresAt: now + body.recovery_expires_in * 1000,
  }
  localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(stored))
  localStorage.setItem(ACCOUNT_TOKEN_KEY, stored.accessToken)
  localStorage.setItem(ACCOUNT_TOKEN_EXPIRES_KEY, String(stored.accessExpiresAt))
  epoch++
  notify()
  return { id: body.user.id, name: body.user.name, phone: { number: body.user.phone }, addresses: [], sessionKind: body.session_kind, phoneVerified: false }
}

/** Returns only a locally current guest access credential; the server remains its authority. */
export function currentGuestToken(): string | null {
  const stored = readGuest()
  if (!stored || stored.accessExpiresAt <= Date.now() || tokenSubject(stored.accessToken) !== stored.user.id ||
      localStorage.getItem(ACCOUNT_TOKEN_KEY) !== stored.accessToken) return null
  return stored.accessToken
}

/** Restores the same guest from its server recovery credential without selecting by phone. */
export async function ensureGuestToken(): Promise<string | null> {
  const current = currentGuestToken()
  if (current) return current
  const stored = readGuest()
  if (!stored || stored.recoveryExpiresAt <= Date.now()) return null
  const snapshot = localStorage.getItem(GUEST_SESSION_KEY)
  if (!snapshot) return null
  if (recovery?.snapshot === snapshot) return recovery.promise
  const startedEpoch = epoch
  const request = recoverGuest(stored, snapshot, startedEpoch)
  recovery = { snapshot, promise: request }
  try {
    return await request
  } finally {
    if (recovery?.promise === request) recovery = null
  }
}

async function recoverGuest(stored: StoredGuest, snapshot: string, startedEpoch: number): Promise<string | null> {
  try {
    const { apiCustomerPublic } = await import("@/api")
    const { data } = await apiCustomerPublic.post<unknown>("/auth/recover", { recovery_token: stored.recoveryToken }, { timeout: 5000 })
    if (localStorage.getItem(GUEST_SESSION_KEY) !== snapshot || epoch !== startedEpoch) return null
    storeGuestSession(data, startedEpoch, stored.user.id)
    return currentGuestToken()
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 401 && epoch === startedEpoch && localStorage.getItem(GUEST_SESSION_KEY) === snapshot) {
      clearGuestSession()
    } else if (epoch === startedEpoch && localStorage.getItem(GUEST_SESSION_KEY) === snapshot) {
      unavailableSnapshot = snapshot
      notify()
    }
    return null
  }
}

/** Applies an access refresh only to the same current guest and original credential. */
export function storeGuestAccessRefresh(value: unknown, originalToken: string): boolean {
  const stored = readGuest()
  const parsed = z.object({ token: credentialSchema, expires_in: z.number().int().positive().max(86400) }).safeParse(value)
  if (!stored || !parsed.success || stored.accessToken !== originalToken || localStorage.getItem(ACCOUNT_TOKEN_KEY) !== originalToken ||
      tokenSubject(parsed.data.token) !== stored.user.id) return false
  stored.accessToken = parsed.data.token
  stored.accessExpiresAt = Date.now() + parsed.data.expires_in * 1000
  localStorage.setItem(GUEST_SESSION_KEY, JSON.stringify(stored))
  localStorage.setItem(ACCOUNT_TOKEN_KEY, stored.accessToken)
  localStorage.setItem(ACCOUNT_TOKEN_EXPIRES_KEY, String(stored.accessExpiresAt))
  epoch++
  notify()
  return true
}
